/**
 * runtimeSelectors.js — Semantic query layer over the runtime state.
 *
 * All selectors are pure, synchronous, and deterministic.
 * No ML, no embeddings — only heuristic pattern matching over
 * the event timeline.
 *
 * These will become the retrieval signals that feed:
 *  - Memory packet construction (kevia-memory-architecture)
 *  - Context-aware prompt injection (orchestrateKevia)
 *  - Proactive suggestion generation (resolveKeviaSuggestions)
 *  - Future tool selection in the agent layer (kevia-agent)
 */

import { getTimeline, getRecentFromTimeline } from "./sessionTimeline";
import { getState }                           from "./runtimeStore";
import { RT_EVENTS }                          from "./runtimeEvents";
// Dep note: kevia-signals imports from sessionTimeline only — no cycle
import { getActiveSignals, SIGNALS }          from "../kevia-signals";

/* ─── Entity selectors ────────────────────────────────────────────────── */

/** Returns the currently active entity (last OPEN_ENTITY + not cleared). */
export function getCurrentEntity() {
  return getState().activeEntity;
}

/** Scans the timeline for the most recent entity visit. */
export function getLastVisitedEntity() {
  const timeline = getTimeline();
  for (let i = timeline.length - 1; i >= 0; i--) {
    const ev = timeline[i];
    if (ev.type === RT_EVENTS.OPEN_ENTITY && ev.entityId) {
      return { type: ev.entityType, id: ev.entityId, name: ev.entityName };
    }
  }
  return null;
}

/* ─── Module selectors ────────────────────────────────────────────────── */

/**
 * Returns the last N distinct modules visited, in visit order (oldest → newest).
 * Deduplicates consecutive repeats but preserves transitions.
 *
 * @param {number} limit
 * @returns {string[]}
 */
export function getRecentModules(limit = 5) {
  const timeline = getTimeline();
  const result   = [];
  let   lastMod  = null;

  for (const ev of timeline) {
    if (ev.module && ev.module !== lastMod) {
      result.push(ev.module);
      lastMod = ev.module;
    }
  }

  return result.slice(-limit);
}

/* ─── Intent selectors ────────────────────────────────────────────────── */

/**
 * Intent signal map — each entry is a scoring function that returns true
 * when a given event is consistent with the intent. Scored over last 8 events.
 *
 * Threshold: at least 2 matching events to declare a dominant intent.
 * This avoids false positives from a single incidental navigation.
 *
 * Future: replace with lightweight embedding similarity against intent embeddings
 * once SEMANTIC_MEMORY_READY is true.
 */
const INTENT_SCORERS = {
  seguimiento_familiar: (ev) =>
    (ev.type === RT_EVENTS.OPEN_ENTITY && (ev.entityType === "family" || ev.entityType === "person")) ||
    ev.module === "familias" ||
    ev.module === "personas" ||
    ev.module === "visitas" ||
    (ev.type === RT_EVENTS.CREATE_RECORD && (ev.entityType === "visit" || ev.entityType === "family")),

  supervision_administrativa: (ev) =>
    ev.module === "operaciones" ||
    ev.module === "auditoría"   ||
    ev.module === "usuarios"    ||
    ev.type === RT_EVENTS.EXECUTE_ACTION_CHIP,

  preparacion_predicacion: (ev) =>
    ev.module === "predicaciones" ||
    ev.metadata?.intent === "sermon",

  intercesion: (ev) =>
    ev.module === "oraciones" ||
    (ev.type === RT_EVENTS.OPEN_ENTITY && ev.entityType === "prayer"),

  gestion_congregacional: (ev) =>
    ev.module === "iglesias" ||
    (ev.type === RT_EVENTS.OPEN_ENTITY && ev.entityType === "church"),
};

/**
 * Returns the dominant operational intent based on recent activity,
 * or null if the pattern is too ambiguous.
 *
 * @returns {string|null}
 */
export function getDominantIntent() {
  const recent = getRecentFromTimeline(8);
  if (recent.length < 2) return null;

  let maxScore = 0;
  let dominant = null;

  for (const [intent, scoreFn] of Object.entries(INTENT_SCORERS)) {
    const score = recent.filter((ev) => {
      try { return scoreFn(ev); } catch { return false; }
    }).length;

    if (score > maxScore) {
      maxScore = score;
      dominant = intent;
    }
  }

  return maxScore >= 2 ? dominant : null;
}

/** Alias — semantic name for use in context injection. */
export function getRecentOperationalFocus() {
  return getDominantIntent();
}

/* ─── Compound selectors ──────────────────────────────────────────────── */

/**
 * Returns a list of recently visited entity names (unique, newest last, max N).
 * Used to generate memory-enriched conversation starters.
 *
 * @param {number} limit
 * @returns {{ type: string, name: string }[]}
 */
export function getRecentEntityNames(limit = 3) {
  const timeline = getTimeline();
  const seen     = new Set();
  const result   = [];

  for (let i = timeline.length - 1; i >= 0 && result.length < limit; i--) {
    const { entityId, entityType, entityName } = timeline[i];
    if (entityId && !seen.has(entityId)) {
      seen.add(entityId);
      result.unshift({ type: entityType, name: entityName });
    }
  }

  return result;
}

/**
 * Builds a compact runtime context string for injection into orchestrateKevia.
 * Capped at ~160 chars — prioritizes operational mode > module trail > entity.
 * Uses short [rt: prefix to save token budget.
 *
 * Example output:
 *   [rt: modo:seguimiento | familias→visitas | Familia González]
 *
 * Returns empty string if no meaningful context is available yet.
 */
export function buildRuntimeContext() {
  const mode       = getOperationalMode();
  const recentMods = getRecentModules(3);
  const entity     = getCurrentEntity();

  const parts = [];

  if (mode) {
    parts.push(`modo:${mode.replace(/_/g, " ").toLowerCase()}`);
  } else {
    const intent = getDominantIntent();
    if (intent) parts.push(`enfoque:${intent.replace(/_/g, " ")}`);
  }

  if (recentMods.length >= 2) {
    parts.push(recentMods.join("→"));
  }

  if (entity?.name) {
    parts.push(entity.name);
  }

  if (parts.length === 0) return "";

  const raw = `[rt: ${parts.join(" | ")}]`;
  return raw.length > 160 ? raw.slice(0, 157) + "…]" : raw;
}

/* ─── FASE 18 — Intent evolution selectors ────────────────────────────── */

/**
 * Returns emerging intent from the most recent 4 events.
 * Lower threshold (1 match) — catches intent before it becomes dominant.
 *
 * @returns {string|null}
 */
export function getEmergingIntent() {
  const recent = getRecentFromTimeline(4);
  if (recent.length === 0) return null;

  let maxScore = 0;
  let emerging = null;

  for (const [intent, scoreFn] of Object.entries(INTENT_SCORERS)) {
    const score = recent.filter((ev) => {
      try { return scoreFn(ev); } catch { return false; }
    }).length;

    if (score > maxScore) {
      maxScore = score;
      emerging = intent;
    }
  }

  return maxScore >= 1 ? emerging : null;
}

/**
 * Returns a confidence score (0–1) for the current dominant intent.
 * Based on the ratio of matching events in the last 8.
 *
 * @returns {number}
 */
export function getIntentConfidence() {
  const recent = getRecentFromTimeline(8);
  if (recent.length < 2) return 0;

  const intent = getDominantIntent();
  if (!intent) return 0;

  const scoreFn = INTENT_SCORERS[intent];
  if (!scoreFn) return 0;

  const matches = recent.filter((ev) => { try { return scoreFn(ev); } catch { return false; } }).length;
  return Math.min(1, matches / Math.max(recent.length, 1));
}

/* ─── FASE 18 — Operational mode ──────────────────────────────────────── */

/**
 * Operational modes — the semantic state Kevia uses to
 * adapt thinking phrases, suggestions, and insight cards.
 */
export const OPERATIONAL_MODES = Object.freeze({
  PASTORAL_CARE:       "PASTORAL_CARE",
  ADMIN_REVIEW:        "ADMIN_REVIEW",
  DISTRICT_OVERSIGHT:  "DISTRICT_OVERSIGHT",
  FOLLOW_UP:           "FOLLOW_UP",
  MESSAGE_PREPARATION: "MESSAGE_PREPARATION",
  ANALYTICS_REVIEW:    "ANALYTICS_REVIEW",
});

/**
 * Derives the current operational mode from active signals + intent.
 *
 * Priority order: high-score signals → intent → module → null
 *
 * @returns {string|null} — one of OPERATIONAL_MODES or null
 */
export function getOperationalMode() {
  const signals = getActiveSignals();
  const topSignal = signals[0]; // already sorted by score desc

  if (topSignal) {
    if (topSignal.id === SIGNALS.AUDIT_INVESTIGATION) return OPERATIONAL_MODES.ADMIN_REVIEW;
    if (topSignal.id === SIGNALS.ADMIN_ATTENTION)     return OPERATIONAL_MODES.ADMIN_REVIEW;
    if (topSignal.id === SIGNALS.FOLLOW_UP_RISK || topSignal.id === SIGNALS.MEMBER_RECOVERY_FLOW) {
      return OPERATIONAL_MODES.FOLLOW_UP;
    }
    if (topSignal.id === SIGNALS.INACTIVE_FAMILY_PATTERN) return OPERATIONAL_MODES.PASTORAL_CARE;
    if (topSignal.id === SIGNALS.PRAYER_FOCUS)            return OPERATIONAL_MODES.PASTORAL_CARE;
    if (topSignal.id === SIGNALS.HIGH_VISIT_ACTIVITY)     return OPERATIONAL_MODES.FOLLOW_UP;
    if (topSignal.id === SIGNALS.LEADERSHIP_REVIEW)       return OPERATIONAL_MODES.DISTRICT_OVERSIGHT;
  }

  const intent = getDominantIntent();
  if (intent === "preparacion_predicacion")   return OPERATIONAL_MODES.MESSAGE_PREPARATION;
  if (intent === "supervision_administrativa") return OPERATIONAL_MODES.ADMIN_REVIEW;
  if (intent === "seguimiento_familiar")      return OPERATIONAL_MODES.PASTORAL_CARE;
  if (intent === "intercesion")               return OPERATIONAL_MODES.PASTORAL_CARE;
  if (intent === "gestion_congregacional")    return OPERATIONAL_MODES.DISTRICT_OVERSIGHT;

  const { currentModule } = getState();
  if (currentModule === "operaciones" || currentModule === "auditoría") return OPERATIONAL_MODES.ADMIN_REVIEW;
  if (currentModule === "predicaciones") return OPERATIONAL_MODES.MESSAGE_PREPARATION;

  return null;
}

/* ─── FASE 18 — Contextual greeting ──────────────────────────────────── */

/**
 * Returns a pastoral, context-aware greeting description for the welcome screen.
 * Prioritizes: entity context → operational mode → module → null
 *
 * @param {object|null} entityContext — from KeviaContext.registerContext()
 * @returns {string|null}
 */
export function getContextualGreeting(entityContext) {
  if (entityContext?.name) {
    const typeLabel = {
      family:  "familia",
      person:  "persona",
      church:  "iglesia",
      visit:   "visita",
      prayer:  "oración",
    }[entityContext.type] ?? "registro";
    return `Tienes la ${typeLabel} ${entityContext.name} abierta. Puedo ayudarte con seguimiento pastoral o reflexión.`;
  }

  const mode = getOperationalMode();
  if (mode === OPERATIONAL_MODES.PASTORAL_CARE)       return "Hay señales de seguimiento familiar en tu sesión.";
  if (mode === OPERATIONAL_MODES.FOLLOW_UP)           return "Sesión enfocada en seguimiento pastoral activo.";
  if (mode === OPERATIONAL_MODES.ADMIN_REVIEW)        return "Sesión administrativa activa. ¿En qué necesitas ayuda?";
  if (mode === OPERATIONAL_MODES.MESSAGE_PREPARATION) return "Preparando contenido para predicación y enseñanza.";
  if (mode === OPERATIONAL_MODES.DISTRICT_OVERSIGHT)  return "Revisando el panorama pastoral del distrito.";

  return null;
}

/* ─── FASE 19 — Session drift + context scoring ───────────────────────── */

/**
 * Detects abrupt shifts in operational intent across the session.
 *
 * A high-drift session (many different modules visited in sequence)
 * signals that historical runtime context should be weighted less
 * heavily in prompt injection — the user's focus has shifted.
 *
 * @returns {{ score: number, level: "low"|"medium"|"high", distinctModules: number, totalModules: number }}
 */
export function detectSessionDrift() {
  const modules  = getRecentModules(8);
  const total    = modules.length;
  const distinct = new Set(modules).size;
  const score    = total >= 2 ? distinct / total : 0;

  return {
    score,
    level:          score >= 0.75 ? "high" : score >= 0.4 ? "medium" : "low",
    distinctModules: distinct,
    totalModules:    total,
  };
}

/**
 * Scores the session context by recency — gives more weight to recent events.
 *
 * Prepared for future semantic retrieval where recent context must be
 * ranked higher than stale history in memory packet assembly.
 *
 * @returns {{ dominantModule: string|null, dominantEntity: string|null, recencyBias: number, totalEvents: number }}
 */
export function scoreRecentContext() {
  const timeline = getTimeline();
  const n        = timeline.length;

  if (n === 0) {
    return { dominantModule: null, dominantEntity: null, recencyBias: 0, totalEvents: 0 };
  }

  const modWeights    = {};
  const entityWeights = {};

  // Linear recency weight: event at index i gets weight (i+1)/n
  // Oldest event → near 0; newest → near 1.0
  for (let i = 0; i < n; i++) {
    const ev = timeline[i];
    const w  = (i + 1) / n;
    if (ev.module)     modWeights[ev.module]        = (modWeights[ev.module]        ?? 0) + w;
    if (ev.entityName) entityWeights[ev.entityName] = (entityWeights[ev.entityName] ?? 0) + w;
  }

  const topOf = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

  // recencyBias: weight of the last-5 events relative to the full timeline
  const last5 = Math.min(5, n);
  const recencyBias = last5 / n;

  return {
    dominantModule:  topOf(modWeights),
    dominantEntity:  topOf(entityWeights),
    recencyBias,
    totalEvents:     n,
  };
}

/* ─── FASE 18 — Session heatmap ───────────────────────────────────────── */

/**
 * Returns a compact heatmap of the session: top modules, entities, actions.
 * Used for dev debug + future semantic memory compression.
 *
 * @returns {{ topModules: string[], topEntities: string[], topActions: string[] }}
 */
export function getSessionHeatmap() {
  const timeline = getTimeline();

  const modCount    = {};
  const entityCount = {};
  const actionCount = {};

  for (const ev of timeline) {
    if (ev.module)     modCount[ev.module]         = (modCount[ev.module]         ?? 0) + 1;
    if (ev.entityName) entityCount[ev.entityName]  = (entityCount[ev.entityName]  ?? 0) + 1;
    if (ev.type)       actionCount[ev.type]        = (actionCount[ev.type]        ?? 0) + 1;
  }

  const top = (obj, n) =>
    Object.entries(obj).sort((a, b) => b[1] - a[1]).slice(0, n).map(([k]) => k);

  return {
    topModules:   top(modCount,    3),
    topEntities:  top(entityCount, 3),
    topActions:   top(actionCount, 3),
  };
}
